const CONTROL_CHAR_PATTERN = /[\u0000-\u001F\u007F]/;
const ENCODED_CRLF_PATTERN = /%(?:0d|0a)/i;
const ALLOWED_IMAGE_DATA_URL_PREFIX_PATTERN = /^data:image\/(?:png|jpe?g|gif|webp);base64,/i;
const BASE64_BODY_PATTERN = /^(?:[a-z0-9+/]{4})*(?:[a-z0-9+/]{2}==|[a-z0-9+/]{3}=)?$/i;

function hasInvalidControlChars(value: string): boolean {
  return CONTROL_CHAR_PATTERN.test(value);
}

function parseAbsoluteUrl(raw: unknown): URL | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (hasInvalidControlChars(trimmed)) return null;
  if (trimmed.startsWith('//')) return null;
  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
}

export function sanitizeHttpUrl(raw: unknown): string | null {
  const parsed = parseAbsoluteUrl(raw);
  if (!parsed) return null;
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
}

function sanitizeSameOriginRootRelativeNavigationPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (hasInvalidControlChars(trimmed)) return null;
  if (ENCODED_CRLF_PATTERN.test(trimmed)) return null;
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.includes('\\')) return null;
  return trimmed;
}

function sanitizeNavigationUrlWithOrigin(raw: unknown, origin: string): string | null {
  if (typeof raw === 'string' && ENCODED_CRLF_PATTERN.test(raw.trim())) return null;

  const safeAbsolute = sanitizeHttpUrl(raw);
  if (safeAbsolute) return safeAbsolute;

  const safeRootRelative = sanitizeSameOriginRootRelativeNavigationPath(raw);
  if (!safeRootRelative) return null;

  try {
    const resolved = new URL(safeRootRelative, origin);
    if (resolved.origin !== origin) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

export function sanitizeNavigationUrl(raw: unknown): string | null {
  // Navigation only (e.g. OAuth redirect). Do not reuse this for href/src sinks.
  // href/src should continue using strict absolute URL sanitizers.
  const fallbackOrigin = 'http://localhost';
  const runtimeOrigin = typeof globalThis.location?.origin === 'string' ? globalThis.location.origin : fallbackOrigin;
  return sanitizeNavigationUrlWithOrigin(raw, runtimeOrigin);
}

export function sanitizeImageUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (hasInvalidControlChars(trimmed)) return null;
  if (trimmed.startsWith('//')) return null;
  if (ALLOWED_IMAGE_DATA_URL_PREFIX_PATTERN.test(trimmed)) {
    const base64Body = trimmed.slice(trimmed.indexOf(',') + 1);
    if (base64Body.length >= 4 && BASE64_BODY_PATTERN.test(base64Body)) return trimmed;
    return null;
  }
  const parsed = parseAbsoluteUrl(trimmed);
  if (!parsed) return null;
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
}

export function getSourceLinkKind(raw: unknown): 'gmail' | 'slack' | 'calendar' | 'generic' {
  const safeUrl = sanitizeHttpUrl(raw);
  if (!safeUrl) return 'generic';
  let hostname = '';
  try {
    hostname = new URL(safeUrl).hostname.toLowerCase();
  } catch {
    return 'generic';
  }
  if (hostname === 'mail.google.com' || hostname === 'gmail.com') return 'gmail';
  if (hostname === 'calendar.google.com') return 'calendar';
  if (hostname === 'slack.com' || hostname.endsWith('.slack.com')) return 'slack';
  return 'generic';
}
